import { useEffect, useRef, useState, useCallback } from 'react'
import { Observable, Subscription } from 'rxjs'

interface UseApiOptions<T> {
  onSuccess?: (data: T) => void
  onError?: (error: any) => void
  skip?: boolean
  deps?: any[]
}

export function useApi<T>(
  apiCall: () => Observable<any>,
  options: UseApiOptions<T> = {},
) {
  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<any>(null)
  const subscriptionRef = useRef<Subscription | null>(null)
  const isMountedRef = useRef(true)
  const isFetchingRef = useRef(false)
  const requestIdRef = useRef(0)

  // Memoize the API call function with proper dependencies
  const memoizedApiCall = useCallback(apiCall, options.deps || [])

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  useEffect(() => {
    if (options.skip) return

    const currentRequestId = ++requestIdRef.current

    const fetchData = () => {
      if (!isMountedRef.current || currentRequestId !== requestIdRef.current)
        return

      isFetchingRef.current = true
      setLoading(true)
      setError(null)

      // Cleanup previous subscription if it exists
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe()
        subscriptionRef.current = null
      }

      subscriptionRef.current = memoizedApiCall().subscribe({
        next: (response) => {
          if (
            !isMountedRef.current ||
            currentRequestId !== requestIdRef.current
          )
            return

          if (!response || !response.response) {
            throw new Error('No response received')
          }

          const responseData = response.response as T
          setData(responseData)
          setLoading(false)
          isFetchingRef.current = false
          options.onSuccess?.(responseData)
        },
        error: (err) => {
          if (
            !isMountedRef.current ||
            currentRequestId !== requestIdRef.current
          )
            return

          console.error('API Error:', err)
          setError(err instanceof Error ? err.message : 'An error occurred')
          setLoading(false)
          isFetchingRef.current = false
          options.onError?.(err)
        },
        complete: () => {
          if (
            !isMountedRef.current ||
            currentRequestId !== requestIdRef.current
          )
            return
          isFetchingRef.current = false
        },
      })
    }

    fetchData()

    return () => {
      if (currentRequestId === requestIdRef.current) {
        if (subscriptionRef.current) {
          subscriptionRef.current.unsubscribe()
          subscriptionRef.current = null
        }
        isFetchingRef.current = false
      }
    }
  }, [memoizedApiCall, options.skip])

  return { data, loading, error }
}
